/**
 * LocalStorage utilities for persisting game data
 */

const STORAGE_KEYS = {
  HIGH_SCORES: 'matematikspillet_high_scores',
  GAME_SESSION: 'matematikspillet_session',
  SETTINGS: 'matematikspillet_settings',
  PROBLEM_HISTORY: 'matematikspillet_problem_history',
  STATISTICS: 'matematikspillet_statistics',
};

/**
 * High Scores Management
 */
export function getHighScores() {
  try {
    const scores = localStorage.getItem(STORAGE_KEYS.HIGH_SCORES);
    return scores ? JSON.parse(scores) : [];
  } catch (error) {
    console.error('Error loading high scores:', error);
    return [];
  }
}

export function addHighScore(scoreEntry) {
  try {
    const scores = getHighScores();
    scores.push({
      ...scoreEntry,
      date: new Date().toISOString(),
    });

    // Sort by score descending, keep top 10
    scores.sort((a, b) => b.score - a.score);
    const topScores = scores.slice(0, 10);

    localStorage.setItem(STORAGE_KEYS.HIGH_SCORES, JSON.stringify(topScores));
    return topScores;
  } catch (error) {
    console.error('Error saving high score:', error);
    return getHighScores();
  }
}

export function isHighScore(score) {
  const scores = getHighScores();
  if (scores.length < 10) return true;
  return score > scores[scores.length - 1].score;
}

export function clearHighScores() {
  try {
    localStorage.removeItem(STORAGE_KEYS.HIGH_SCORES);
  } catch (error) {
    console.error('Error clearing high scores:', error);
  }
}

/**
 * Game Session Management (for resume functionality)
 */
export function saveGameSession(gameState) {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_SESSION, JSON.stringify({
      ...gameState,
      savedAt: new Date().toISOString(),
    }));
  } catch (error) {
    console.error('Error saving game session:', error);
  }
}

export function loadGameSession() {
  try {
    const session = localStorage.getItem(STORAGE_KEYS.GAME_SESSION);
    if (!session) return null;

    const parsed = JSON.parse(session);
    const savedAt = new Date(parsed.savedAt);
    const now = new Date();
    const hoursSince = (now - savedAt) / (1000 * 60 * 60);

    // Only restore session if saved within last 24 hours
    if (hoursSince > 24) {
      clearGameSession();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error loading game session:', error);
    return null;
  }
}

export function clearGameSession() {
  try {
    localStorage.removeItem(STORAGE_KEYS.GAME_SESSION);
  } catch (error) {
    console.error('Error clearing game session:', error);
  }
}

/**
 * Settings Management
 */
export function getSettings() {
  try {
    const settings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return settings ? JSON.parse(settings) : {
      soundEnabled: true,
      animationsEnabled: true,
    };
  } catch (error) {
    console.error('Error loading settings:', error);
    return {
      soundEnabled: true,
      animationsEnabled: true,
    };
  }
}

export function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings:', error);
  }
}

export function updateSetting(key, value) {
  const settings = getSettings();
  settings[key] = value;
  saveSettings(settings);
  return settings;
}

/**
 * Problem History Management
 * Tracks all problems attempted for learning analytics
 */
const MAX_HISTORY_ITEMS = 100; // Keep last 100 problems

export function getProblemHistory() {
  try {
    const history = localStorage.getItem(STORAGE_KEYS.PROBLEM_HISTORY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error loading problem history:', error);
    return [];
  }
}

export function addProblemToHistory(problemEntry) {
  try {
    const history = getProblemHistory();
    history.push({
      ...problemEntry,
      timestamp: new Date().toISOString(),
    });

    // Keep only last MAX_HISTORY_ITEMS
    const trimmedHistory = history.slice(-MAX_HISTORY_ITEMS);

    localStorage.setItem(STORAGE_KEYS.PROBLEM_HISTORY, JSON.stringify(trimmedHistory));

    // Update statistics
    updateStatistics(problemEntry);

    return trimmedHistory;
  } catch (error) {
    console.error('Error saving problem to history:', error);
    return getProblemHistory();
  }
}

export function clearProblemHistory() {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROBLEM_HISTORY);
  } catch (error) {
    console.error('Error clearing problem history:', error);
  }
}

/**
 * Statistics Management
 * Aggregated stats for analytics
 */
export function getStatistics() {
  try {
    const stats = localStorage.getItem(STORAGE_KEYS.STATISTICS);
    return stats ? JSON.parse(stats) : {
      totalProblems: 0,
      correctAnswers: 0,
      wrongAnswers: 0,
      byOperation: {
        plus: { correct: 0, wrong: 0 },
        minus: { correct: 0, wrong: 0 },
        gange: { correct: 0, wrong: 0 },
      },
      byDifficulty: {
        let: { correct: 0, wrong: 0 },
        medium: { correct: 0, wrong: 0 },
        svært: { correct: 0, wrong: 0 },
      },
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error loading statistics:', error);
    return null;
  }
}

function updateStatistics(problemEntry) {
  try {
    const stats = getStatistics();

    stats.totalProblems += 1;

    if (problemEntry.isCorrect) {
      stats.correctAnswers += 1;
      stats.byOperation[problemEntry.operationType].correct += 1;
      stats.byDifficulty[problemEntry.difficulty].correct += 1;
    } else {
      stats.wrongAnswers += 1;
      stats.byOperation[problemEntry.operationType].wrong += 1;
      stats.byDifficulty[problemEntry.difficulty].wrong += 1;
    }

    stats.lastUpdated = new Date().toISOString();

    localStorage.setItem(STORAGE_KEYS.STATISTICS, JSON.stringify(stats));
    return stats;
  } catch (error) {
    console.error('Error updating statistics:', error);
    return getStatistics();
  }
}

export function clearStatistics() {
  try {
    localStorage.removeItem(STORAGE_KEYS.STATISTICS);
  } catch (error) {
    console.error('Error clearing statistics:', error);
  }
}
